/**
 * components/Toast/ToastHost.web.tsx
 * ──────────────────────────────────────────────────────────────────────────
 * Web toast host.
 *
 * react-native-web renders `Modal` into a body-level portal at z-index 9999,
 * so a toast sitting in the normal React tree appears BEHIND any open modal.
 * Since most validation in this product fires from inside a modal ("Every
 * spend requires a category"), that would hide the message exactly when it
 * matters. The fix is to portal the toast layer into its own fixed, full-page
 * div above that stacking context.
 *
 * The layer itself is `pointer-events: none` so it can never block a click;
 * the card inside re-enables pointer events for itself.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Toast from 'react-native-toast-message';
import toastConfig from './toastConfig';

export function ToastHost() {
  const layerRef = useRef<HTMLDivElement | null>(null);

  if (!layerRef.current && typeof document !== 'undefined') {
    const el = document.createElement('div');
    el.style.position = 'fixed';
    el.style.inset = '0';
    el.style.zIndex = '2147483646'; // above react-native-web's Modal (9999)
    el.style.pointerEvents = 'none';
    layerRef.current = el;
  }

  useEffect(() => {
    const el = layerRef.current;
    if (!el) return undefined;
    document.body.appendChild(el);
    return () => {
      if (el.parentNode) el.parentNode.removeChild(el);
    };
  }, []);

  if (!layerRef.current) return null;

  return createPortal(
    <Toast config={toastConfig} position="bottom" bottomOffset={20} />,
    layerRef.current,
  );
}

export default ToastHost;
